import { useMemo, useState } from 'react';
import {
  CoverageBadge,
  DensityBar,
  GateBadge,
  Icon,
  MetaGrid,
  MetaItem,
  Panel,
  RuleId,
  Screen,
  ScreenHeader,
  StatTile,
  StatusBadge,
  SummaryTiles,
  Uncited,
  // Stat removed — replaced by SummaryTiles/StatTile
  Workspace,
  WorkspacePane,
} from '@garnet/ui';
import type { StudioContext } from '../studio.js';
import { StateBlock } from '../components.js';

function CoverageMatrix({
  ctx,
  selected,
  onSelect,
}: {
  ctx: StudioContext;
  selected: string;
  onSelect: (key: string) => void;
}) {
  const { api } = ctx;
  const domains = api.domains();
  return (
    <div style={{ overflowX: 'auto' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `132px repeat(${domains.length}, minmax(120px, 1fr))`,
          gap: 0,
          minWidth: 920,
        }}
      >
        <div
          style={{
            position: 'sticky',
            left: 0,
            zIndex: 3,
            background: 'var(--bg-surface)',
            borderBottom: '1px solid var(--border)',
          }}
        />
        {domains.map((d) => (
          <div
            key={d.key}
            style={{ padding: '9px 10px', borderBottom: '1px solid var(--border)', borderLeft: '1px solid var(--border-subtle)' }}
          >
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--fg-1)', lineHeight: 1.2 }}>{d.short}</div>
            <div className="label" style={{ fontSize: 8.5, marginTop: 2 }}>
              {d.key === 'sanctions' ? 'export' : d.key.split('_')[0]}
            </div>
          </div>
        ))}

        {api.jurisdictions().map((j) => (
          <div key={j.code} style={{ display: 'contents' }}>
            <div
              style={{
                position: 'sticky',
                left: 0,
                zIndex: 2,
                background: 'var(--bg-surface)',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '0 10px',
                borderBottom: '1px solid var(--border-subtle)',
                borderRight: '1px solid var(--border)',
              }}
            >
              <span className="mono" style={{ fontSize: 12, fontWeight: 600, color: 'var(--garnet-text)', width: 22 }}>
                {j.code}
              </span>
              <span style={{ fontSize: 11, color: 'var(--fg-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {j.name}
              </span>
            </div>
            {domains.map((d) => {
              const key = `${j.code}:${d.key}`;
              const c = api.coverageCell(key);
              if (!c) return <div key={key} />;
              const sel = selected === key;
              return (
                <div
                  key={key}
                  className={`cov-cell ${c.state} ${sel ? 'sel' : ''}`}
                  onClick={() => onSelect(key)}
                  role="button"
                  aria-pressed={sel}
                  style={{ minHeight: 62, padding: '8px 9px', borderLeft: '1px solid var(--border-subtle)' }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <span
                      className="mono"
                      style={{
                        fontSize: 16,
                        fontWeight: 500,
                        color: c.state === 'empty' ? 'var(--fg-3)' : 'var(--fg-1)',
                        letterSpacing: '-0.02em',
                      }}
                    >
                      {c.state === 'empty' ? '—' : c.rules}
                    </span>
                    {c.gate === 'abstain' && c.state !== 'empty' && (
                      <span className="mono" style={{ fontSize: 8.5, color: 'var(--abstain-text)', letterSpacing: '0.04em' }}>
                        ABST
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {c.state !== 'empty' ? (
                      <span
                        className="label"
                        style={{ fontSize: 8, color: c.state === 'thin' ? 'var(--cov-thin)' : 'var(--fg-3)' }}
                      >
                        {c.state === 'thin' ? 'thin · gap' : `${c.topics} topics`}
                      </span>
                    ) : (
                      <span className="label" style={{ fontSize: 8, color: 'var(--fg-3)' }}>
                        no coverage
                      </span>
                    )}
                    <DensityBar count={c.rules} max={200} state={c.state} />
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

function CellDetail({ ctx, cellKey }: { ctx: StudioContext; cellKey: string }) {
  const { api } = ctx;
  const [jc = '', dk = ''] = cellKey.split(':');
  const c = api.coverageCell(cellKey);
  const dom = api.domains().find((d) => d.key === dk);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- recompute when the cell changes
  const rules = useMemo(() => api.rulesForCell(jc, dk), [cellKey]);
  if (!c || !dom) return null;

  return (
    <div className="fade-in" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap)' }}>
      <Panel
        flush
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <span className="mono" style={{ color: 'var(--garnet-text)' }}>
              {jc}
            </span>
            <Icon name="chevR" size={12} color="var(--fg-3)" />
            <span>{dom.name}</span>
          </span>
        }
        right={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <CoverageBadge state={c.state} />
            <button className="btn btn-sm mono" onClick={() => ctx.createRule(jc, dk)}>
              <Icon name="plus" size={12} />
              New rule
            </button>
          </div>
        }
      >
        {rules.length === 0 ? (
          <div style={{ padding: 18 }}>
            <StateBlock
              kind="empty"
              title="empty cell · no rules authored"
              body={`This (${jc}, ${dom.short}) cell holds no rules. The coverage gate returns no-coverage for every query here. This is the work to do — author the first rule to open the cell.`}
              action={
                <button className="btn btn-primary btn-sm mono" onClick={() => ctx.createRule(jc, dk)}>
                  <Icon name="plus" size={12} />
                  Author the first rule
                </button>
              }
            />
          </div>
        ) : (
          <div>
            <table className="tbl">
              <thead>
                <tr>
                  <th>Rule identifier</th>
                  <th>Title</th>
                  <th>Output</th>
                  <th>Effective</th>
                  <th>Source</th>
                  <th style={{ textAlign: 'right' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {rules.map((r) => (
                  <tr key={r.ruleId} onClick={() => ctx.openRule(r.ruleId)}>
                    <td>
                      <RuleId id={r.ruleId} version={r.version} />
                    </td>
                    <td style={{ color: 'var(--fg-2)', maxWidth: 220, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {r.title}
                    </td>
                    <td>
                      <span className="tag">{r.outputType}</span>
                    </td>
                    <td className="mono metatext">{r.effectiveFrom}</td>
                    <td>
                      {r.citations.length === 0 ? (
                        <Uncited />
                      ) : (
                        <span className="mono metatext">{r.citations.length} cited</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <StatusBadge status={r.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Panel title="Cell" right={<GateBadge gate={c.gate} />}>
        <MetaGrid labelWidth={130}>
          <MetaItem label="State">
            <CoverageBadge state={c.state} />
          </MetaItem>
          <MetaItem label="Rules">
            <span className="mono">{c.state === 'empty' ? '0' : c.rules}</span>
          </MetaItem>
          <MetaItem label="Topics">
            <span className="mono">{c.topics}</span>
          </MetaItem>
          <MetaItem label="Serving gate">
            <span className="mono">{c.gate}</span>
          </MetaItem>
          <MetaItem label="Coverage owner">{c.owner}</MetaItem>
          <MetaItem label="No-coverage rate">
            <span className="mono" style={{ color: c.noCoverage > 15 ? 'var(--status-warn-text)' : 'var(--fg-1)' }}>
              {c.noCoverage}%
            </span>
          </MetaItem>
          <MetaItem label="Abstain rate">
            <span className="mono">{c.abstain}%</span>
          </MetaItem>
        </MetaGrid>
        <hr className="hr" style={{ margin: '12px 0' }} />
        <div className="label" style={{ marginBottom: 8 }}>
          The work to do
        </div>
        {c.state === 'covered' ? (
          <p className="metatext">Cell is covered and answering. Watch for source changes that flag dependent rules.</p>
        ) : c.state === 'thin' ? (
          <p className="metatext">
            Thin cell — answers where a rule matches, abstains and flags otherwise. {c.noCoverage}% of queries hit
            no-coverage. Ranked in the gap backlog.
          </p>
        ) : (
          <p className="metatext">
            Empty cell. Every query returns no-coverage. Prioritised by query demand and regulatory risk.
          </p>
        )}
      </Panel>
    </div>
  );
}

export function RuleStore({
  ctx,
  selectedCell,
  setSelectedCell,
}: {
  ctx: StudioContext;
  selectedCell: string;
  setSelectedCell: (key: string) => void;
}) {
  const { api } = ctx;
  const [q, setQ] = useState('');

  const cells = api.coverageList();
  const covered = cells.filter((c) => c.state === 'covered').length;
  const thin = cells.filter((c) => c.state === 'thin').length;
  const empty = cells.filter((c) => c.state === 'empty').length;
  const total = cells.length;
  const pct = total ? Math.round((covered / total) * 100) : 0;

  const query = q.trim().toLowerCase();
  const searchHits = query
    ? api.rules().filter((r) => `${r.ruleId} ${r.title}`.toLowerCase().includes(query))
    : null;

  const headerActions = (
    <>
      <div style={{ position: 'relative', width: 260 }}>
        <Icon name="search" size={14} color="var(--fg-3)" style={{ position: 'absolute', left: 11, top: 9 }} />
        <input
          className="inp mono"
          placeholder="rule reference or citation…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ paddingLeft: 32, fontSize: 12 }}
        />
      </div>
      <button
        className="btn btn-primary mono"
        onClick={() => {
          const [jc = 'EU', dk = 'medical'] = (selectedCell || 'EU:medical').split(':');
          ctx.createRule(jc, dk);
        }}
      >
        <Icon name="plus" size={13} />
        New rule
      </button>
    </>
  );

  const summary = (
    <SummaryTiles min={108} style={{ minWidth: 470 }}>
      <StatTile label="Coverage" value={`${pct}%`} valueStyle={{ color: 'var(--garnet)' }} />
      <StatTile label="Covered" value={covered} valueStyle={{ color: 'var(--status-pass-text)' }} />
      <StatTile label="Thin" value={thin} valueStyle={{ color: 'var(--status-warn-text)' }} />
      <StatTile label="Empty" value={empty} />
    </SummaryTiles>
  );

  if (searchHits) {
    return (
      <Screen>
        <ScreenHeader
          eyebrow="§ Rule store"
          title="Coverage matrix"
          lead="Jurisdiction × domain. Empty and thin cells are first-class — they are the backlog."
          actions={headerActions}
        />
        <Panel flush title={`Search · ${searchHits.length} rule${searchHits.length === 1 ? '' : 's'}`}>
          <div style={{ overflowY: 'auto', maxHeight: 460 }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th>Rule identifier</th>
                  <th>Title</th>
                  <th>Domain</th>
                  <th>Source</th>
                  <th style={{ textAlign: 'right' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {searchHits.map((r) => (
                  <tr key={r.ruleId} onClick={() => ctx.openRule(r.ruleId)}>
                    <td>
                      <RuleId id={r.ruleId} version={r.version} />
                    </td>
                    <td style={{ color: 'var(--fg-2)' }}>{r.title}</td>
                    <td>
                      <span className="tag">{r.domain}</span>
                    </td>
                    <td>
                      {r.citations.length === 0 ? (
                        <Uncited />
                      ) : (
                        <span className="mono metatext">{r.citations.length} cited</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <StatusBadge status={r.status} />
                    </td>
                  </tr>
                ))}
                {searchHits.length === 0 && (
                  <tr>
                    <td colSpan={5} className="metatext" style={{ padding: 18 }}>
                      No rule matches “{q}”.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Panel>
      </Screen>
    );
  }

  return (
    <Screen workspace>
      <ScreenHeader
        eyebrow="§ Rule store"
        title="Coverage matrix"
        lead="Jurisdiction × domain. Empty and thin cells are first-class — they are the backlog."
        actions={headerActions}
      />
      <Workspace style={{ gridTemplateColumns: '1fr 332px' }}>
        <WorkspacePane>
          <div
            className="pane-sticky"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px var(--pad-x)', flexWrap: 'wrap' }}
          >
            {summary}
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <span style={{ width: 9, height: 9, borderRadius: 2, background: 'var(--cov-covered-bg)', border: '1px solid var(--cov-covered-border)' }} />
                <span className="metatext">covered</span>
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <span style={{ width: 9, height: 9, borderRadius: 2, background: 'var(--cov-thin-bg)', border: '1px solid var(--cov-thin-border)' }} />
                <span className="metatext">thin</span>
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <span style={{ width: 9, height: 9, borderRadius: 2, background: 'var(--cov-empty-bg)', border: '1px solid var(--border)' }} />
                <span className="metatext">empty</span>
              </span>
            </div>
          </div>
          <div style={{ padding: 'var(--pad-x)' }}>
            <CoverageMatrix ctx={ctx} selected={selectedCell} onSelect={setSelectedCell} />
          </div>
        </WorkspacePane>
        <WorkspacePane flush>
          {selectedCell ? (
            <CellDetail ctx={ctx} cellKey={selectedCell} />
          ) : (
            <StateBlock kind="empty" title="Select a cell" body="Pick a cell in the matrix to see its rules." />
          )}
        </WorkspacePane>
      </Workspace>
    </Screen>
  );
}
